import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useI18n } from '../context/I18nContext';
import { APPS_DATA } from '../data/apps';
import { FaqCategory } from '../types';
import { SeoHead } from '../components/common/SeoHead';
import { NotFoundPage } from './NotFoundPage';
import { SupportCategoryList } from '../components/support/SupportCategoryList';
import { Accordion } from '../components/ui/Accordion';
import { ContactBlock } from '../components/support/ContactBlock';

export const AppSupportPage: React.FC = () => {
  const { appSlug } = useParams<{ appSlug: string }>();
  const { resolveText, t } = useI18n();
  const [selectedCategory, setSelectedCategory] = useState<FaqCategory | null>(null);

  const app = APPS_DATA.find((a) => a.slug === appSlug);

  if (!app) {
    return <NotFoundPage customMessage="지원 대상 앱을 찾을 수 없습니다." />;
  }

  // Filter FAQs if a category is selected
  const filteredFaqs = (app.faqs || []).filter((f) => {
    if (!selectedCategory) return true;
    return f.category === selectedCategory;
  });

  const accordionItems = filteredFaqs.map((f) => ({
    id: f.id,
    title: resolveText(f.question),
    content: resolveText(f.answer),
  }));

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: filteredFaqs.map((f) => ({
      '@type': 'Question',
      name: resolveText(f.question),
      acceptedAnswer: {
        '@type': 'Answer',
        text: resolveText(f.answer),
      },
    })),
  };

  return (
    <div className="py-10 sm:py-16 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
      <SeoHead
        title={`${app.name} 공식 고객지원 센터 | Official Support`}
        description={`${app.name} 공식 고객지원 페이지입니다. 사용 방법, 계정 문제, 오류 해결 가이드 및 개발자 1:1 문의를 확인하세요.`}
        canonicalPath={`/support/${app.slug}`}
        schemaJson={faqSchema}
      />

      {/* Breadcrumb / Back Link */}
      <div className="flex items-center gap-2 text-xs font-medium text-slate-500">
        <Link to="/support" className="inline-flex items-center gap-1 hover:text-slate-800 transition-colors">
          <ArrowLeft className="w-3.5 h-3.5" />
          <span>전체 고객지원 허브</span>
        </Link>
        <span>/</span>
        <span className="text-slate-800 font-semibold">{app.name}</span>
      </div>

      {/* Official Header (Apple Store Guideline 1.5 Compliant) */}
      <div className="bg-white border border-slate-200/90 rounded-2xl p-6 sm:p-8 shadow-xs flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
        <div className="flex items-center gap-5">
          <img
            src={app.iconUrl}
            alt={`${app.name} Icon`}
            className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl object-cover shadow-xs border border-slate-100 shrink-0"
          />
          <div className="space-y-1">
            <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
              {app.name} 지원
            </h1>
            <p className="text-xs text-slate-500 font-mono">
              {t.support.versionPrefix} {app.currentVersion} • {t.support.lastUpdatedPrefix} {app.lastUpdated}
            </p>
          </div>
        </div>
      </div>

      {/* Quick Troubleshooting Categories */}
      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-slate-900 tracking-tight">
            {t.support.quickCategoriesTitle}
          </h2>
          {selectedCategory && (
            <button
              type="button"
              onClick={() => setSelectedCategory(null)}
              className="text-xs text-primary-700 hover:text-primary-800 font-semibold"
            >
              전체 보기
            </button>
          )}
        </div>
        <SupportCategoryList
          onSelectCategory={(cat) => setSelectedCategory(cat === selectedCategory ? null : cat)}
          activeCategory={selectedCategory}
        />
      </section>

      {/* FAQ Accordion Section */}
      <section className="space-y-4">
        <h2 className="text-lg font-bold text-slate-900 tracking-tight">
          {t.support.faqTitle}
          {selectedCategory && <span className="text-sm font-normal text-slate-500 ml-2">(카테고리 필터 적용됨)</span>}
        </h2>
        {accordionItems.length > 0 ? (
          <div className="bg-white border border-slate-200/90 rounded-2xl px-6 shadow-xs">
            <Accordion items={accordionItems} defaultOpenId={accordionItems[0]?.id} />
          </div>
        ) : (
          <div className="p-8 text-center bg-slate-50 rounded-2xl border border-slate-200 text-slate-500 text-sm">
            해당 카테고리에 등록된 질문이 없습니다.{' '}
            <button
              type="button"
              onClick={() => setSelectedCategory(null)}
              className="text-primary-700 font-medium underline"
            >
              전체 보기
            </button>
          </div>
        )}
      </section>

      {/* Direct Contact & Account Deletion Block */}
      <section>
        <ContactBlock appName={app.name} supportEmail={app.supportEmail} />
      </section>
    </div>
  );
};
